import type {
  PersistedClient,
  Persister,
} from '@tanstack/react-query-persist-client'
import { Dexie } from 'dexie'
import { captureException } from '@sentry/nextjs'

type StoredClient = PersistedClient & {
  id: string
}

class ReactQueryDB extends Dexie {
  client!: Dexie.Table<StoredClient, string>

  constructor() {
    super('ReactQueryDB')
    this.version(1).stores({
      client: '++id',
    })
  }
}

/**
 * Creates an Indexed DB persister
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API
 */
export const createDexiePersister = (key = 'reactQuery'): Persister => {
  const database = new ReactQueryDB()

  return {
    persistClient: async (client: PersistedClient) => {
      try {
        await database.transaction('rw', database.client, async () => {
          // We must use a constant id, otherwise Dexie will automatically
          // keep creating new entries in the database every time this method is called
          await database.client.put({ ...client, id: key }, key)
        })
      } catch (error) {
        captureException(error)
      }
    },
    restoreClient: async () => {
      try {
        return await database.transaction('r', database.client, async () => {
          return await database.client.get(key)
        })
      } catch (error) {
        captureException(error)
      }
    },
    removeClient: async () => {
      try {
        await database.transaction('rw', database.client, async () => {
          await database.client.delete(key)
        })
      } catch (error) {
        captureException(error)
      }
    },
  }
}

export const clearIDB = async () => {
  try {
    await Dexie.delete('ReactQueryDB')
  } catch (error) {
    captureException(error)
  }
}
